package br.gov.go.pge.permanenciaweb.site.components

/*
 * Minimal civil-date type for the browser.
 *
 * The site does not depend on kotlinx-datetime (only the JVM side uses `java.time`), and the range
 * picker needs exactly four operations: ISO parsing/formatting, day arithmetic, weekday and month
 * length. This is the well-known "days from civil" algorithm, kept small and unit-tested instead of
 * pulling a multiplatform dependency into the JS bundle.
 */

data class CivilDate(val year: Int, val month: Int, val day: Int) : Comparable<CivilDate> {

    init {
        require(month in 1..12) { "mês inválido: $month" }
        require(day in 1..lengthOfMonth(year, month)) { "dia inválido: $day" }
    }

    /** Days since 1970-01-01 (Howard Hinnant's algorithm). */
    fun toEpochDay(): Long {
        val y = if (month <= 2) year - 1 else year
        val era = (if (y >= 0) y else y - 399) / 400
        val yoe = y - era * 400
        val doy = (153 * (if (month > 2) month - 3 else month + 9) + 2) / 5 + day - 1
        val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
        return era.toLong() * 146097L + doe.toLong() - 719468L
    }

    fun plusDays(days: Long): CivilDate = fromEpochDay(toEpochDay() + days)

    /** 1 = Monday … 7 = Sunday. */
    val isoDayOfWeek: Int
        get() = ((toEpochDay() + 3) % 7 + 7).toInt() % 7 + 1

    /** 0 = Monday … 6 = Sunday (the grid's column index, Monday-first). */
    val columnIndex: Int
        get() = isoDayOfWeek - 1

    val iso: String
        get() = "${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"

    /** `dd/MM/yyyy`, the format the rest of the UI uses. */
    val br: String
        get() = "${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/$year"

    fun withDay(day: Int): CivilDate = CivilDate(year, month, day)

    override fun compareTo(other: CivilDate): Int = toEpochDay().compareTo(other.toEpochDay())

    override fun toString(): String = iso

    companion object {
        fun fromEpochDay(epochDay: Long): CivilDate {
            val z = epochDay + 719468L
            val era = (if (z >= 0) z else z - 146096L) / 146097L
            val doe = z - era * 146097L
            val yoe = (doe - doe / 1460L + doe / 36524L - doe / 146096L) / 365L
            val y = yoe + era * 400L
            val doy = doe - (365L * yoe + yoe / 4L - yoe / 100L)
            val mp = (5L * doy + 2L) / 153L
            val d = (doy - (153L * mp + 2L) / 5L + 1L).toInt()
            val m = (if (mp < 10L) mp + 3L else mp - 9L).toInt()
            return CivilDate((y + if (m <= 2) 1L else 0L).toInt(), m, d)
        }

        /** Parses `yyyy-MM-dd`; null when malformed or out of range. */
        fun parseOrNull(value: String?): CivilDate? {
            val raw = value?.trim().orEmpty()
            if (raw.length != 10) return null
            val year = raw.substring(0, 4).toIntOrNull() ?: return null
            val month = raw.substring(5, 7).toIntOrNull() ?: return null
            val day = raw.substring(8, 10).toIntOrNull() ?: return null
            return runCatching { CivilDate(year, month, day) }.getOrNull()
        }

        fun lengthOfMonth(year: Int, month: Int): Int = when (month) {
            2 -> if (isLeap(year)) 29 else 28
            4, 6, 9, 11 -> 30
            else -> 31
        }

        fun isLeap(year: Int): Boolean = (year % 4 == 0 && year % 100 != 0) || year % 400 == 0

        /** Browser-local "today" (the app is single-timezone: America/Sao_Paulo). */
        fun today(): CivilDate {
            val now = kotlin.js.Date()
            return CivilDate(now.getFullYear().toInt(), now.getMonth().toInt() + 1, now.getDate().toInt())
        }

        val MONTH_NAMES = listOf(
            "janeiro", "fevereiro", "março", "abril", "maio", "junho",
            "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
        )

        /** Monday-first weekday headers. */
        val WEEKDAY_LABELS = listOf("seg", "ter", "qua", "qui", "sex", "sáb", "dom")
    }
}
